import { Collapse, Modal, Tabs, Tag, Typography } from "antd";
import { FC, ReactNode } from "react";

const { Text, Paragraph } = Typography;

interface IOperatorGuideEntry {
  name: string;
  syntax: string;
  description: string;
  example: string;
  notes?: string[];
}

const RESPONSE_OPERATORS: IOperatorGuideEntry[] = [
  {
    name: "AND",
    syntax: "AND(response_variable_1, response_variable_2, ...)",
    description:
      "True only when EVERY listed response variable has an answer. Arguments are response variables (from the #-response autocomplete), not question variables. Can also nest another operator's result as one of its arguments.",
    example: "AND(Minor_waivfail_Granted, Minor_waivfail_Age_18_or_older)",
  },
  {
    name: "OR",
    syntax: "OR(response_variable_1, response_variable_2, ...)",
    description:
      "True when AT LEAST ONE listed response variable has an answer. Same argument rules as AND.",
    example: "OR(Minor_waivfail_Granted, Minor_waivfail_Denied)",
  },
  {
    name: "EQUAL",
    syntax: 'EQUAL(response_variable, "value")',
    description:
      "True if the response variable's answer text matches value (case-insensitive). Takes exactly two parameters — a response variable, then a literal comparison value.",
    example: 'EQUAL(Minor_waivfail_Type, "Judicial")',
  },
  {
    name: "NOTEQUAL",
    syntax: 'NOTEQUAL(response_variable, "value")',
    description:
      "True if the response variable's answer text does NOT match value. Same two-parameter shape as EQUAL.",
    example: 'NOTEQUAL(Minor_waivfail_Type, "Judicial")',
  },
  {
    name: "COMPARE",
    syntax: "COMPARE(response_variable, operator, value)",
    description:
      "General comparison — takes exactly three parameters. operator must be one of =, !=, <, >, >=, <=. = and != behave exactly like EQUAL/NOTEQUAL; the four inequality operators do a numeric comparison against value.",
    example: "COMPARE(Minor_waivfail_Age, >=, 18)",
    notes: [
      "For the numeric operators (<, >, >=, <=), value should be a plain number — non-numeric answers won't match.",
    ],
  },
];

const QUESTION_OPERATORS: IOperatorGuideEntry[] = [
  {
    name: "QAND",
    syntax: "QAND(question_variable)",
    description:
      "Shorthand for AND-ing every response option under one question. Takes exactly one question variable (from the @-question autocomplete) — true only if all of that question's response options have an answer.",
    example: "QAND(Minor_waivfail)",
  },
  {
    name: "QOR",
    syntax: "QOR(question_variable)",
    description:
      "Shorthand for OR-ing every response option under one question. Takes exactly one question variable — true if any of that question's response options have an answer.",
    example: "QOR(Minor_waivfail)",
  },
  {
    name: "QHASANSWER",
    syntax: "QHASANSWER(question_variable)",
    description:
      "True if the question has been answered at all, regardless of which option was chosen. Takes exactly one question variable.",
    example: "QHASANSWER(Minor_waivfail)",
  },
  {
    name: "QHASNOTANSWER",
    syntax: "QHASNOTANSWER(question_variable)",
    description:
      "True if the question has NOT been answered. Takes exactly one question variable.",
    example: "QHASNOTANSWER(Minor_waivfail)",
  },
];

const OTHER_OPERATORS: IOperatorGuideEntry[] = [
  {
    name: "IMPORT",
    syntax: "IMPORT(rank_name)",
    description:
      "Reuses another rank's already-computed result (pass/fail) as if it were a variable — useful for building tiers where a higher rank requires everything a lower rank requires, plus more. Takes exactly one rank name (use the $ shortcut below to pick from this project's ranks).",
    example: "AND(IMPORT(Gold), QOR(Additional_criteria))",
    notes: [
      "A rank can only IMPORT an equal-or-lower rank — importing a higher rank is rejected.",
      "The imported rank's own equation must be valid and saved for this to resolve correctly when the ranking runs.",
    ],
  },
];

const SHORTCUTS: { trigger: string; label: string; description: string }[] = [
  {
    trigger: "@",
    label: "Question variables",
    description:
      "Insert a question variable, for QAND/QOR/QHASANSWER/QHASNOTANSWER.",
  },
  {
    trigger: "#",
    label: "Response variables",
    description: "Insert a response variable, for AND/OR/EQUAL/NOTEQUAL/COMPARE.",
  },
  {
    trigger: ":",
    label: "Operators",
    description: "Insert any operator name from this guide, pre-filled with ().",
  },
  {
    trigger: "$",
    label: "Ranks",
    description:
      "Insert another rank's name, for IMPORT — only shows ranks that are valid to import (excludes the current tab and any higher rank).",
  },
];

const MODAL_BODY_MAX_HEIGHT = 420;

const ScrollableTabBody: FC<{ children: ReactNode }> = ({ children }) => (
  <div style={{ maxHeight: MODAL_BODY_MAX_HEIGHT, overflowY: "auto" }}>
    {children}
  </div>
);

const OperatorList: FC<{ entries: IOperatorGuideEntry[] }> = ({ entries }) => (
  <Collapse
    items={entries.map((entry) => ({
      key: entry.name,
      label: (
        <span>
          <Tag color="blue">{entry.name}</Tag>
          <Text code>{entry.syntax}</Text>
        </span>
      ),
      children: (
        <>
          <Paragraph style={{ marginBottom: 8 }}>
            {entry.description}
          </Paragraph>
          <Paragraph style={{ marginBottom: entry.notes ? 8 : 0 }}>
            <Text strong>Example: </Text>
            <Text code>{entry.example}</Text>
          </Paragraph>
          {entry.notes && (
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {entry.notes.map((note, index) => (
                <li key={index}>
                  <Text type="secondary">{note}</Text>
                </li>
              ))}
            </ul>
          )}
        </>
      ),
    }))}
  />
);

const OperatorGuideModal: FC<{ open: boolean; onClose: () => void }> = ({
  open,
  onClose,
}) => {
  return (
    <Modal
      title="Ranking Equation — Operator Guide"
      open={open}
      onCancel={onClose}
      onOk={onClose}
      cancelButtonProps={{ style: { display: "none" } }}
      okText="Close"
      width={720}
    >
      <Paragraph type="secondary" style={{ marginBottom: 12 }}>
        Every equation is built from these operators. Nest one inside another
        (e.g. <Text code>AND(OR(a, b), c)</Text>) to combine logic.
      </Paragraph>

      <Tabs
        items={[
          {
            key: "response",
            label: "Response",
            children: (
              <ScrollableTabBody>
                <OperatorList entries={RESPONSE_OPERATORS} />
              </ScrollableTabBody>
            ),
          },
          {
            key: "question",
            label: "Question",
            children: (
              <ScrollableTabBody>
                <OperatorList entries={QUESTION_OPERATORS} />
              </ScrollableTabBody>
            ),
          },
          {
            key: "other",
            label: "Other",
            children: (
              <ScrollableTabBody>
                <OperatorList entries={OTHER_OPERATORS} />
                <Text strong style={{ display: "block", margin: "16px 0 8px" }}>
                  Typing shortcuts
                </Text>
                <Collapse
                  items={SHORTCUTS.map((shortcut) => ({
                    key: shortcut.trigger,
                    label: (
                      <span>
                        <Tag color="green">{shortcut.trigger}</Tag>
                        <Text strong style={{ marginLeft: 5 }}>
                          {shortcut.label}
                        </Text>
                      </span>
                    ),
                    children: (
                      <Text type="secondary">{shortcut.description}</Text>
                    ),
                  }))}
                />
              </ScrollableTabBody>
            ),
          },
        ]}
      />
    </Modal>
  );
};

export default OperatorGuideModal;
